import { useState, useEffect, useCallback } from "react";
import { Library, Download, Send, FileText, ShieldCheck, Users, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { PanelLoading } from "../../components/SmartImage";
import { TangibleAsset } from "../../components/TangibleAsset";
import { toast } from "sonner";

export function LibraryTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/library").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);

  const download = async (it) => {
    setBusy(it.key + ":dl");
    try {
      const resp = await api.get(`/admin/library/${it.key}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${it.title}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) { toast.error("Download failed"); } finally { setBusy(""); }
  };

  const pushDrop = async (it) => {
    if (!window.confirm(`Push "${it.title}" as a DRAFT drop/resource? You'll review & publish it from Drops.`)) return;
    setBusy(it.key + ":push");
    try {
      await api.post(`/admin/library/${it.key}/push-drop`);
      toast.success("Created as a draft drop — review & publish from the Drops tab");
    } catch (e) { toast.error("Could not push as drop"); } finally { setBusy(""); }
  };

  if (!items) return <PanelLoading label="Gathering the library" />;
  const admin = items.filter((i) => i.audience === "admin");
  const member = items.filter((i) => i.audience === "member");

  const Card = ({ it }) => (
    <div data-testid={`library-${it.key}`} className="panel p-5 flex items-start gap-5">
      <TangibleAsset variant="codex" title={it.title} label={it.audience === "admin" ? "CODEX" : "GIFT"} width="w-16 shrink-0" />
      <div className="flex-1 min-w-0">
        <h3 className="font-display text-xl text-cream leading-tight">{it.title}</h3>
        <p className="text-textMuted text-sm mt-1">{it.desc}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button data-testid={`library-download-${it.key}`} onClick={() => download(it)} disabled={busy.startsWith(it.key)} className="btn-gold text-xs">
            {busy === it.key + ":dl" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Download className="w-3 h-3"/>} Download
          </button>
          <button data-testid={`library-push-${it.key}`} onClick={() => pushDrop(it)} disabled={busy.startsWith(it.key)} className="btn-ghost text-xs">
            {busy === it.key + ":push" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>} Push as Drop
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="library-tab">
      <div className="overline mb-2 flex items-center gap-2"><Library className="w-4 h-4"/> // THE LIBRARY — every codex & gift in one place</div>
      <p className="text-textMuted text-sm mb-8">Download any PDF, or push it as a draft drop/resource for members. All resources are shareable when you're ready.</p>

      <div className="overline mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold"/> // ADMIN CODICES</div>
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {admin.map((it) => <Card key={it.key} it={it} />)}
      </div>

      <div className="overline mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-gold"/> // MEMBER ASSETS</div>
      <div className="grid md:grid-cols-2 gap-4">
        {member.map((it) => <Card key={it.key} it={it} />)}
      </div>
    </div>
  );
}
